import React from "react";
import styled from "styled-components";
import { groupBySection, QUESTION_TYPE_LABELS, branchNo } from "./data";
import { ChecklistDetail, SpecifyDetail, questionLabel } from "./QuestionMeta";
import { narrativeViewerStyles, NARRATIVE_BOX_WIDTH } from "./narrativeStyles";
import { withTableWrappers } from "./narrativeHtml";

// Read-only rendering of one questionnaire's content — a published SFQ_Version or
// the working copy awaiting approval. Shared by VersionHistory, the pending-change
// viewer, and the Overview/Questions tabs while a publish request is in flight.

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

const Narrative = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 6px;
  color: #212529;

  ${narrativeViewerStyles}
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
  margin: 14px 0 6px;
`;

const QRow = styled.div`
  padding: 6px 0;
  border-bottom: 1px solid #f1f3f5;
  font-size: 13px;
  color: #495057;
`;

// 하위 문항. 들여쓴 자리에 세로선을 세워 어느 상위 문항에 딸렸는지 잇는다.
// "┖"가 하위라는 것만 말해 주므로, 어느 상위인지는 이 선과 위치가 맡는다.
const SubQRow = styled(QRow)`
  padding-left: 24px;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 8px; top: 6px; bottom: 6px;
    border-left: 2px solid #e9ecef;
  }
`;

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex; align-items: center; justify-content: center;
`;

// 내러티브 상자(본문 폭 + 패딩 + 테두리)가 Body 패딩 안에 그대로 들어가야
// 에디터에서 보던 줄바꿈이 유지된다. 좁히면 상자가 먼저 줄어든다.
const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  width: ${NARRATIVE_BOX_WIDTH + 2 + 40}px;
  max-width: 92vw;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #dee2e6;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #212529;
`;

const CloseBtn = styled.button`
  background: none; border: none;
  font-size: 18px; color: #6c757d;
  cursor: pointer; line-height: 1;
  &:hover { color: #212529; }
`;

const Body = styled.div`
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
`;

const SubLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin: 8px 0;
`;

export function NarrativeView({ html }) {
  return (
    <Narrative
      dangerouslySetInnerHTML={{ __html: withTableWrappers(html) || "<em>(비어 있음)</em>" }}
    />
  );
}

export function QuestionsView({ questions }) {
  const sections = groupBySection(questions || []);
  if (!Object.keys(sections).length) return <Empty>No questions.</Empty>;

  // An option's showsQuestions may point at a question in another section, so the
  // lookup is built across the whole version rather than per section. groupBySection
  // hangs the section number off the map key, so it is put back on the question here —
  // questionLabel names the target by section and question number.
  const byKey = {};
  for (const [secNo, sec] of Object.entries(sections)) {
    for (const q of sec.questions) byKey[q.key] = { ...q, sectionNo: secNo };
  }
  const resolveQuestion = (key) => questionLabel(byKey[key], byKey);

  return (
    <>
      {Object.entries(sections).map(([secNo, sec]) => (
        <div key={secNo}>
          <SectionTitle>{sec.title || `Section ${secNo}`}</SectionTitle>
          {sec.questions.map((q) => {
            // 하위 문항은 "┖ 1"로 찍고 한 단계 들여쓴다. 자기 번호는 상위마다 1부터
            // 다시 시작해 바로 위 상위 문항과 같은 숫자가 되므로, 하위라는 표시가 없으면
            // 같은 번호가 잇달아 찍힌 것처럼 읽힌다.
            const Row = q.parentKey ? SubQRow : QRow;
            return (
            <Row key={q.key}>
              <b>{branchNo(q)}</b>. {q.text}{" "}
              <span style={{ color: "#adb5bd", fontSize: 12 }}>
                ({QUESTION_TYPE_LABELS[q.type] || q.type})
              </span>
              {q.isRequired === false && (
                <span style={{ color: "#adb5bd", fontSize: 11 }}> · 선택 응답</span>
              )}
              <SpecifyDetail
                type={q.type}
                hasSpecify={q.hasSpecify}
                specifyLabel={q.specifyLabel}
                specifyWhen={q.specifyWhen}
              />
              {q.type === "checklist" && (
                <ChecklistDetail
                  options={q.options}
                  singleSelect={q.singleSelect}
                  resolveQuestion={resolveQuestion}
                />
              )}
            </Row>
            );
          })}
        </div>
      ))}
    </>
  );
}

// `loading` covers the version fetch; content arrives on a later render.
export function ContentModal({ title, subtitle, narrativeHtml, questions, loading, onClose }) {
  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        <Body>
          {loading ? (
            <Empty>Loading...</Empty>
          ) : (
            <>
              {subtitle && (
                <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 12 }}>{subtitle}</div>
              )}
              <SubLabel>Study Overview</SubLabel>
              <NarrativeView html={narrativeHtml} />
              <div style={{ height: 20 }} />
              <SubLabel>Questions</SubLabel>
              <QuestionsView questions={questions} />
            </>
          )}
        </Body>
      </Modal>
    </Backdrop>
  );
}
